/**
 * Phase 8 T-06 §7 A / J — Smart Match canvas page.
 *
 * Hydrates the pipeline intake stashed by `SmartMatchIntakePage` and mounts
 * the three-column `MatchCanvasLayout`. If the stash is missing (e.g. user
 * navigated here directly) we redirect to `/smart-match/new` per §7 J.
 */

import { useCallback, useEffect, useMemo } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { MatchCanvasLayout } from './components/MatchCanvasLayout';
import { readPipelineIntake } from './lib/matchCanvasPipeline';

export function SmartMatchCanvasPage() {
  const { pipelineId = '' } = useParams<{ pipelineId: string }>();
  const navigate = useNavigate();
  const intake = useMemo(() => (pipelineId ? readPipelineIntake(pipelineId) : null), [pipelineId]);

  useEffect(() => {
    if (!pipelineId || !intake) return;
    document.title = `Smart Match · ${intake.prompt.slice(0, 60)}`;
  }, [pipelineId, intake]);

  // "+ New chat" in the toolbar sends the user back to the intake
  // page (fresh prompt, fresh pipeline id). Sourcing wires the same
  // shape — keeping Smart Match + Sourcing symmetric means the
  // toolbar renders identically on both canvases (post user-feedback,
  // 21-Apr).
  const onNewChat = useCallback(() => {
    navigate('/smart-match/new');
  }, [navigate]);

  if (!pipelineId) {
    return <Navigate to="/smart-match/new" replace />;
  }
  if (!intake) {
    return <Navigate to="/smart-match/new" replace />;
  }

  return (
    <div className="-mx-6 -my-4 flex min-h-0 flex-1 flex-col">
      {/*
       * When the user selected "External (paid sources)" on the intake page
       * we route through the C-41 streaming runner so the agent can dispatch
       * `source.*` tool calls to configured connectors (Naukri, LinkedIn, etc.)
       * and surface results inline in the chat column via AgentSourcingResults.
       * The legacy runner only calls Ollama locally — it cannot reach external APIs.
       */}
      <MatchCanvasLayout
        pipelineId={pipelineId}
        intake={intake}
        agentRunnerVariant={intake.source === 'external' ? 'streaming' : 'legacy'}
        onNewChat={onNewChat}
      />
    </div>
  );
}
